import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { getUserPublicData, userService } from '../services/userService';
import { UserPublicData } from '../types/user';
import { profileImageCache } from '../utils/imageCache';

// Cache entry interface
interface CachedUserProfile extends UserPublicData {
  cachedAt: number;
  userId: string;
}

// Store interface
export interface UserProfileStore {
  // State
  profiles: Record<string, CachedUserProfile>;
  isLoading: boolean;
  batchLoadingIds: string[];
  lastCleanup: number;
  
  // Actions
  getProfile: (userId: string) => CachedUserProfile | null;
  batchFetchProfiles: (userIds: string[]) => Promise<void>;
  invalidateProfile: (userId: string) => void;
  clearExpiredProfiles: () => void;
  isProfileExpired: (userId: string) => boolean;
  
  // Privacy-specific actions
  updatePrivacySetting: (userId: string, isPrivate: boolean) => Promise<void>;
  getPrivacyStatus: (userId: string) => boolean | null;
  
  // Internal actions
  setProfile: (userId: string, profileData: UserPublicData) => void;
  setLoading: (loading: boolean) => void;
  addToBatchLoading: (userId: string) => void;
  removeFromBatchLoading: (userId: string) => void;
}

// Constants
const CACHE_TTL = 60 * 60 * 1000; // 1 hour in milliseconds
const CLEANUP_INTERVAL = 15 * 60 * 1000; // 15 minutes in milliseconds
const BATCH_SIZE = 10; // Maximum profiles to fetch in one batch

export const useUserProfileStore = create<UserProfileStore>()(
  persist(
    (set, get) => ({
      // Initial state
      profiles: {},
      isLoading: false,
      batchLoadingIds: [],
      lastCleanup: Date.now(),

      // Get cached profile or null if expired/missing
      getProfile: (userId: string) => {
        const state = get();
        const profile = state.profiles[userId];
        
        if (!profile) return null;
        
        // Check if expired
        const isExpired = Date.now() - profile.cachedAt > CACHE_TTL;
        if (isExpired) {
          // Remove expired profile
          const newProfiles = { ...state.profiles };
          delete newProfiles[userId];
          set({ profiles: newProfiles });
          return null;
        }
        
        return profile;
      },

      // Check if profile is expired without removing it
      isProfileExpired: (userId: string) => {
        const profile = get().profiles[userId];
        if (!profile) return true;
        return Date.now() - profile.cachedAt > CACHE_TTL;
      },

      // Batch fetch multiple profiles efficiently
      batchFetchProfiles: async (userIds: string[]) => {
        const state = get();
        
        // Filter out IDs that are already cached and not expired, or currently being loaded
        const idsToFetch = userIds.filter(id => {
          if (state.batchLoadingIds.includes(id)) return false;
          const profile = state.profiles[id];
          if (!profile) return true;
          return Date.now() - profile.cachedAt > CACHE_TTL;
        });

        if (idsToFetch.length === 0) return;

        // Limit batch size for performance
        const batchIds = idsToFetch.slice(0, BATCH_SIZE);
        
        // Mark IDs as loading
        const newBatchLoadingIds = [...state.batchLoadingIds, ...batchIds];
        
        set({ 
          isLoading: true,
          batchLoadingIds: newBatchLoadingIds
        });

        try {
          // Fetch profiles in parallel
          const profilePromises = batchIds.map(async (userId) => {
            try {
              const profileData = await getUserPublicData(userId);
              return { userId, profileData };
            } catch (error) {
              console.error(`Error fetching profile for user ${userId}:`, error);
              return { userId, profileData: null };
            }
          });

          const results = await Promise.all(profilePromises);
          
          // Update store with fetched profiles
          const currentState = get();
          const newProfiles = { ...currentState.profiles };
          const newBatchLoadingIds = currentState.batchLoadingIds.filter(
            id => !batchIds.includes(id)
          );
          
          results.forEach(({ userId, profileData }) => {
            // Cache the profile if data was retrieved
            if (profileData) {
              newProfiles[userId] = {
                ...profileData,
                userId,
                cachedAt: Date.now()
              };
            }
          });

          set({
            profiles: newProfiles,
            batchLoadingIds: newBatchLoadingIds,
            isLoading: newBatchLoadingIds.length > 0
          });

          // Preload profile images for better UX
          const imageUrls = results
            .filter(({ profileData }) => profileData?.imageUrl)
            .map(({ profileData }) => profileData!.imageUrl!);
          
          if (imageUrls.length > 0) {
            profileImageCache.preloadImages(imageUrls).catch(error => {
              console.debug('Profile image preload failed:', error);
            });
          }

        } catch (error) {
          console.error('Error in batch fetch profiles:', error);
          
          // Clear loading states on error
          const currentState = get();
          const newBatchLoadingIds = currentState.batchLoadingIds.filter(
            id => !batchIds.includes(id)
          );
          
          set({
            batchLoadingIds: newBatchLoadingIds,
            isLoading: newBatchLoadingIds.length > 0
          });
        }
      },

      // Invalidate a specific profile (force refresh on next access)
      invalidateProfile: (userId: string) => {
        const currentProfiles = { ...get().profiles };
        delete currentProfiles[userId];
        set({ profiles: currentProfiles });
      },

      // Clean up expired profiles
      clearExpiredProfiles: () => {
        const state = get();
        const now = Date.now();
        
        // Only run cleanup if enough time has passed
        if (now - state.lastCleanup < CLEANUP_INTERVAL) return;
        
        const newProfiles: Record<string, CachedUserProfile> = {};
        let removedCount = 0;
        
        // Keep only non-expired profiles
        Object.entries(state.profiles).forEach(([userId, profile]) => {
          if (now - profile.cachedAt <= CACHE_TTL) {
            newProfiles[userId] = profile;
          } else {
            removedCount++;
          }
        });
        
        if (removedCount > 0) {
          console.log(`🧹 Cleaned up ${removedCount} expired user profiles`);
          set({ 
            profiles: newProfiles,
            lastCleanup: now
          });
        } else {
          set({ lastCleanup: now });
        }
      },

      // Privacy-specific actions
      updatePrivacySetting: async (userId: string, isPrivate: boolean) => {
        try {
          // Update in database first
          await userService.updateUserProfile(userId, {
            isPrivateProfile: isPrivate,
          });
          
          // Update in store - ensure profile exists in cache
          const currentProfiles = { ...get().profiles };
          if (currentProfiles[userId]) {
            // Update existing profile
            currentProfiles[userId] = {
              ...currentProfiles[userId],
              isPrivateProfile: isPrivate,
              cachedAt: Date.now() // Update cache timestamp
            };
          } else {
            // If profile not cached, create minimal cache entry to ensure consistency
            try {
              const profileData = await getUserPublicData(userId);
              if (profileData) {
                currentProfiles[userId] = {
                  ...profileData,
                  userId,
                  cachedAt: Date.now(),
                  isPrivateProfile: isPrivate // Ensure updated value is set
                };
              }
            } catch (fetchError) {
              console.warn('Could not fetch profile after privacy update:', fetchError);
              // Create minimal entry to prevent inconsistency
              currentProfiles[userId] = {
                userId,
                isPrivateProfile: isPrivate,
                cachedAt: Date.now()
              } as any; // Temporary minimal profile
            }
          }
          set({ profiles: currentProfiles });
        } catch (error) {
          console.error('Error updating privacy setting in store:', error);
          throw error; // Re-throw for component error handling
        }
      },

      getPrivacyStatus: (userId: string) => {
        const profile = get().getProfile(userId);
        return profile?.isPrivateProfile ?? null;
      },

      // Internal: Set a single profile
      setProfile: (userId: string, profileData: UserPublicData) => {
        const newProfiles = { ...get().profiles };
        newProfiles[userId] = {
          ...profileData,
          userId,
          cachedAt: Date.now()
        };
        set({ profiles: newProfiles });
      },

      // Internal: Set loading state
      setLoading: (loading: boolean) => {
        set({ isLoading: loading });
      },

      // Internal: Add to batch loading
      addToBatchLoading: (userId: string) => {
        const currentIds = get().batchLoadingIds;
        if (!currentIds.includes(userId)) {
          set({ batchLoadingIds: [...currentIds, userId] });
        }
      },

      // Internal: Remove from batch loading
      removeFromBatchLoading: (userId: string) => {
        const newBatchLoadingIds = get().batchLoadingIds.filter(id => id !== userId);
        set({ 
          batchLoadingIds: newBatchLoadingIds,
          isLoading: newBatchLoadingIds.length > 0
        });
      }
    }),
    {
      name: 'user-profiles-storage',
      storage: createJSONStorage(() => localStorage),

      // Partial persist - only save profiles and lastCleanup to localStorage
      partialize: (state) => ({
        profiles: state.profiles,
        lastCleanup: state.lastCleanup
      }),

      // Version for future migrations
      version: 1
    }
  )
);

// Hook for easy profile access with automatic fetching
export const useUserProfile = (userId: string | null) => {
  const store = useUserProfileStore();
  
  // Get cached profile
  const cachedProfile = userId ? store.getProfile(userId) : null;
  
  // Auto-fetch if not cached and userId provided
  if (userId && !cachedProfile && !store.batchLoadingIds.includes(userId)) {
    store.batchFetchProfiles([userId]);
  }
  
  return {
    profile: cachedProfile,
    isLoading: userId ? store.batchLoadingIds.includes(userId) : false,
    invalidate: () => userId ? store.invalidateProfile(userId) : undefined
  };
};

// Hook for batch profile management
export const useUserProfiles = () => {
  const store = useUserProfileStore();
  
  return {
    getProfile: store.getProfile,
    batchFetch: store.batchFetchProfiles,
    invalidate: store.invalidateProfile,
    clearExpired: store.clearExpiredProfiles,
    isLoading: store.isLoading,
    profileCount: Object.keys(store.profiles).length
  };
};

// Hook for privacy management
export const useUserPrivacy = (userId: string | null) => {
  const store = useUserProfileStore();
  
  // Get cached profile privacy status
  const privacyStatus = userId ? store.getPrivacyStatus(userId) : null;
  
  // Auto-fetch profile if not cached and userId provided
  if (userId && privacyStatus === null && !store.batchLoadingIds.includes(userId)) {
    store.batchFetchProfiles([userId]);
  }
  
  return {
    isPrivate: privacyStatus,
    updatePrivacy: async (isPrivate: boolean) => {
      if (!userId) throw new Error('User ID is required');
      await store.updatePrivacySetting(userId, isPrivate);
    },
    isLoading: userId ? store.batchLoadingIds.includes(userId) : false
  };
};